import { z } from 'zod';

// ---------------------------------------------------------------------------
// Domain schemas for the first-party customer photo-reviews gallery.
//
// Mirror the `customer_reviews` / `customer_review_photos` tables
// (migration 20260712000001). BIGINT ids → positive ints (NOT UUID). These
// are the write-side / domain contracts (admin CRUD + import); the public
// gallery/aggregate API shapes live in ./api/customer-reviews.ts.
// ---------------------------------------------------------------------------

/**
 * ai_step_status enum values (migration 20260510000001) — runtime zod guard for
 * photo upscale tracking. The TYPE lives in ../types/ai-pipeline.ts
 * (`AiStepStatus`, DB-derived); we don't re-export a colliding type name here.
 */
export const aiStepStatusSchema = z.enum(['pending', 'processing', 'ready', 'failed']);

// ─── customer_reviews ───────────────────────────────────────────────────────

export const customerReviewRowSchema = z.object({
  id: z.number().int().positive(),
  /** NULLABLE FK → wallpapers.id (ON DELETE SET NULL). null = not yet linked. */
  wallpaper_id: z.number().int().positive().nullable(),
  author_name: z.string().nullable(),
  text: z.string().nullable(),
  /** 1..5 integer stars, or null (legacy source has no rating). */
  rating: z.number().int().min(1).max(5).nullable(),
  /** FK → languages.lang_code. Defaults to 'pl'. */
  locale: z.string().min(1),
  /** Publish gate — the single valve for both gallery visibility and SEO contribution. */
  published: z.boolean(),
  sort_order: z.number().int(),
  /** Legacy reviews.id — idempotency key for bulk-import UPSERT. */
  original_review_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type CustomerReviewRow = z.infer<typeof customerReviewRowSchema>;

/** Insert shape — id/created_at/updated_at are DB-managed; defaults optional. */
export const customerReviewInsertSchema = z.object({
  wallpaper_id: z.number().int().positive().nullable().optional(),
  author_name: z.string().nullable().optional(),
  text: z.string().nullable().optional(),
  rating: z.number().int().min(1).max(5).nullable().optional(),
  locale: z.string().min(1).optional(),
  published: z.boolean().optional(),
  sort_order: z.number().int().optional(),
  original_review_id: z.string().nullable().optional(),
});
export type CustomerReviewInsert = z.infer<typeof customerReviewInsertSchema>;

/** Update shape — every field optional (partial patch). */
export const customerReviewUpdateSchema = customerReviewInsertSchema.partial();
export type CustomerReviewUpdate = z.infer<typeof customerReviewUpdateSchema>;

// ─── customer_review_photos ─────────────────────────────────────────────────

export const customerReviewPhotoRowSchema = z.object({
  id: z.number().int().positive(),
  review_id: z.number().int().positive(),
  /** Bucket-relative path (wandero bucket), e.g. reviews/<review_id>/<idx>.webp. */
  storage_path: z.string().min(1),
  sort_order: z.number().int(),
  // upscale-tracking (forward-compat — deferred admin upscale-UI, spec §Scope decision)
  upscale_status: aiStepStatusSchema.nullable(),
  upscale_model: z.string().nullable(),
  upscale_error: z.string().nullable(),
  upscale_cost_usd: z.number().nullable(),
  upscale_external_task_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type CustomerReviewPhotoRow = z.infer<typeof customerReviewPhotoRowSchema>;

export const customerReviewPhotoInsertSchema = z.object({
  review_id: z.number().int().positive(),
  storage_path: z.string().min(1),
  sort_order: z.number().int().optional(),
  upscale_status: aiStepStatusSchema.nullable().optional(),
  upscale_model: z.string().nullable().optional(),
  upscale_error: z.string().nullable().optional(),
  upscale_cost_usd: z.number().nullable().optional(),
  upscale_external_task_id: z.string().nullable().optional(),
});
export type CustomerReviewPhotoInsert = z.infer<typeof customerReviewPhotoInsertSchema>;

export const customerReviewPhotoUpdateSchema = customerReviewPhotoInsertSchema.partial();
export type CustomerReviewPhotoUpdate = z.infer<typeof customerReviewPhotoUpdateSchema>;
